import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Card, formatDateTime, timeAgo, useAdminApi, useConfirm, useToast } from './ui';
import { HEALTH_COLORS, HEALTH_LABELS, HEALTH_PRIORITY, getHealth } from './health';

const CARD_ORDER = ['failing', 'stale', 'new', 'running', 'ok', 'paused'];
const CARD_STYLES = {
  failing: 'border-red-200 bg-red-50 text-red-800',
  stale: 'border-amber-200 bg-amber-50 text-amber-800',
  new: 'border-orange-200 bg-orange-50 text-orange-800',
  running: 'border-blue-200 bg-blue-50 text-blue-800',
  ok: 'border-green-200 bg-green-50 text-green-800',
  paused: 'border-gray-200 bg-gray-50 text-gray-700',
};

function AdminOverview({ data, goTo, reload }) {
  const api = useAdminApi();
  const toast = useToast();

  const rows = useMemo(
    () => data.placeIntegrations.map((integration) => ({ integration, health: getHealth(integration) })),
    [data.placeIntegrations]
  );

  const counts = useMemo(() => {
    const result = Object.fromEntries(CARD_ORDER.map((key) => [key, 0]));
    rows.forEach(({ health }) => { result[health.key] += 1; });
    return result;
  }, [rows]);

  const attention = useMemo(
    () => rows
      .filter(({ health }) => ['failing', 'stale', 'new'].includes(health.key))
      .sort((a, b) => HEALTH_PRIORITY[a.health.key] - HEALTH_PRIORITY[b.health.key])
      .slice(0, 10),
    [rows]
  );

  const unconfirmedUsers = data.users.filter((user) => !user.isConfirmed).length;
  const hiddenRegions = data.regions.filter((region) => region.hide).length;

  const queueRun = async (integration) => {
    try {
      await api(`/admin/integration/${integration.uuid}/queue-run`, { method: 'POST' });
      toast(`Queued ${integration.name || integration.place.name}.`);
      setTimeout(() => reload({ silent: true }), 15000);
    } catch (error) {
      toast(error.message, 'error');
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {CARD_ORDER.map((key) => (
          <button
            key={key}
            onClick={() => goTo('integrations', { health: key })}
            className={`text-left border rounded-lg p-4 hover:shadow-md transition ${CARD_STYLES[key]}`}
          >
            <div className="text-3xl font-bold">{counts[key]}</div>
            <div className="text-sm font-medium">{HEALTH_LABELS[key]}</div>
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card title="Needs attention" className="lg:col-span-2" actions={<Button onClick={() => goTo('integrations', { health: 'attention' })}>View all</Button>}>
          {attention.length === 0 ? (
            <p className="text-sm text-gray-600">Nothing needs attention. 🎉</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {attention.map(({ integration, health }) => (
                <li key={integration.uuid} className="py-3 flex items-center gap-3">
                  <Badge color={HEALTH_COLORS[health.key]}>{HEALTH_LABELS[health.key]}</Badge>
                  <div className="flex-1 min-w-0">
                    <button
                      onClick={() => goTo('integrations', {}, integration.uuid)}
                      className="font-medium text-left hover:underline truncate block max-w-full"
                    >
                      {integration.place.name}
                      <span className="text-gray-500 font-normal"> · {integration.place.region.name}</span>
                    </button>
                    <div className="text-xs text-gray-600 truncate">{health.reasons.join(' · ')}</div>
                  </div>
                  <span className="text-xs text-gray-500 hidden sm:inline">{health.lastRun ? timeAgo(health.lastRun.createdAt) : 'never run'}</span>
                  {integration.status !== 'new' && <Button onClick={() => queueRun(integration)}>Run</Button>}
                  <Button onClick={() => goTo('integrations', {}, integration.uuid)}>Open</Button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <div className="space-y-6">
          <FeedPanel api={api} toast={toast} initialJob={data.feed} />
          <NowCachePanel api={api} toast={toast} />
          <Card title="Also worth a look">
            <ul className="text-sm space-y-2">
              <li className="flex justify-between">
                <span>Users with unconfirmed email</span>
                <button className="font-semibold hover:underline" onClick={() => goTo('users', { filter: 'unconfirmed' })}>{unconfirmedUsers}</button>
              </li>
              <li className="flex justify-between">
                <span>Hidden regions</span>
                <button className="font-semibold hover:underline" onClick={() => goTo('regions')}>{hiddenRegions}</button>
              </li>
              <li className="flex justify-between">
                <span>Regions / users / integrations</span>
                <span className="text-gray-600">{data.regions.length} / {data.users.length} / {data.placeIntegrations.length}</span>
              </li>
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
}

// Regenerates every feed in the background and shows progress by polling /feed/status
function FeedPanel({ api, toast, initialJob }) {
  const confirm = useConfirm();
  const [job, setJob] = useState(initialJob || { running: false });
  const [starting, setStarting] = useState(false);
  const wasRunning = useRef(!!(initialJob && initialJob.running));

  useEffect(() => {
    if (!job.running) return undefined;
    const timer = setInterval(async () => {
      try {
        const { job: latest } = await api('/feed/status');
        setJob(latest);
      } catch {
        // keep polling; transient errors shouldn't interrupt
      }
    }, 3000);
    return () => clearInterval(timer);
  }, [job.running, api]);

  useEffect(() => {
    if (wasRunning.current && !job.running) {
      if (job.error) toast(`Feed regeneration failed: ${job.error}`, 'error');
      else toast(job.result || 'Feeds regenerated.');
    }
    wasRunning.current = job.running;
  }, [job, toast]);

  const start = async () => {
    const ok = await confirm({
      title: 'Regenerate all feeds?',
      message: 'Rebuilds the cached feed for every region, place, organizer and series. This can take a few minutes.',
      confirmLabel: 'Regenerate',
    });
    if (!ok) return;
    setStarting(true);
    try {
      const response = await api('/feed/regenerate', { method: 'POST' });
      setJob(response.job);
    } catch (error) {
      if (error.data && error.data.job) setJob(error.data.job);
      toast(error.message, 'error');
    } finally {
      setStarting(false);
    }
  };

  return (
    <Card title="Feeds" actions={<Button variant="primary" busy={starting || job.running} onClick={start}>{job.running ? 'Running…' : 'Regenerate all'}</Button>}>
      <div className="text-sm text-gray-700 space-y-1">
        {job.running && <p>Started {timeAgo(job.startedAt)}. This page updates automatically.</p>}
        {!job.running && job.finishedAt && !job.error && <p className="text-green-700">{job.result || 'Done'} · {formatDateTime(job.finishedAt)}</p>}
        {!job.running && job.error && <p className="text-red-700">Failed: {job.error}</p>}
        {!job.running && !job.finishedAt && <p className="text-gray-500">No regeneration since the server last started.</p>}
      </div>
    </Card>
  );
}

// The /now listings pages are cached HTML rebuilt hourly; this forces a rebuild after fixing listings
function NowCachePanel({ api, toast }) {
  const [regions, setRegions] = useState(null);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    api('/admin/now-cache').then((data) => setRegions(data.regions)).catch(() => setRegions([]));
  }, [api]);

  const clear = async () => {
    setClearing(true);
    try {
      const data = await api('/admin/now-cache/clear', { method: 'POST' });
      setRegions(data.regions);
      toast(data.message);
    } catch (error) {
      if (error.data && error.data.regions) setRegions(error.data.regions);
      toast(error.message, 'error');
    } finally {
      setClearing(false);
    }
  };

  return (
    <Card title="Now pages" actions={<Button variant="primary" busy={clearing} onClick={clear}>{clearing ? 'Rebuilding…' : 'Clear & rebuild'}</Button>}>
      <div className="text-sm text-gray-700 space-y-1">
        {!regions && <p className="text-gray-500">Loading…</p>}
        {regions && regions.length === 0 && <p className="text-gray-500">Nothing cached yet; pages build on first visit.</p>}
        {regions && regions.map((r) => (
          <p key={r.region} className="flex justify-between gap-2">
            <a href={`/now/${r.region}/`} target="_blank" rel="noreferrer" className="hover:underline">/now/{r.region}</a>
            <span className="text-gray-500">{r.builtAt ? `built ${timeAgo(r.builtAt)}` : 'not built'}</span>
          </p>
        ))}
        <p className="text-xs text-gray-500 pt-1">Rebuilt hourly. Browsers may keep an old copy for up to 5 minutes.</p>
      </div>
    </Card>
  );
}

export default AdminOverview;
